import { useRollbar } from '@rollbar/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { RecordFactory } from '../../models/RecordFactory'
import { defaultPathForLoginResponse } from '../../modules/apiclient'
import { Spinner } from '../Spinner'
import Badge from '../app/design/Badge'
import Button from '../app/design/Button'
import Paragraph from '../app/design/Paragraph'
import { SelectList, SelectListItem } from '../common/SelectList'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { WorkflowPage } from '../workflows/WorkflowPage'

export const ComebackAccountSwitcher = () => {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const rollbar = useRollbar()
    const { logAndDisplayError } = useErrorHandler()

    const { data: associatedAccountUsers } = useQuery({
        queryKey: ['account-switcher-users'],
        queryFn: async () => {
            const result = await RecordFactory.getInstance().fetchRoles('login')
            if (!result.ok) {
                throw result.error
            }
            return result.value
        },
    })

    const [selectedUserID, setSelectedUserID] = useState<string | undefined>(undefined)
    const [isSigningIn, setIsSigningIn] = useState(false)

    const visibleUsers = associatedAccountUsers?.filter((user) => user.canLoginViaAccountSwitcher)

    useEffect(() => {
        if (visibleUsers === undefined || visibleUsers.length > 0) {
            return
        }
        navigate('/login', { replace: true })
    }, [visibleUsers, navigate])

    const listItems = (() => {
        if (visibleUsers === undefined) {
            return undefined
        }

        return visibleUsers.map(
            (user): SelectListItem & { user: typeof user; requiresUpgrade: boolean } => ({
                id: user.id ?? '',
                title: user.entity ?? user.name ?? '',
                subtitle: user.roleSubtitle,
                tertiaryView: (
                    <div className="relative">
                        <ContactAvatar user={user} />
                        {user.roleBadge !== undefined && user.roleBadge > 0 && (
                            <Badge value={user.roleBadge} className="absolute -top-1 -right-1" />
                        )}
                    </div>
                ),
                appearance:
                    (user.solo || user.company) && user.requiresUpgrade ? 'disabled' : 'default',
                user,
                requiresUpgrade: (user.solo || user.company) && user.requiresUpgrade,
            })
        )
    })()
    const selectedItem = listItems?.find((item) => item.id === selectedUserID)

    const handleSwitchAccount = async () => {
        const user = selectedItem?.user
        if (user === undefined || user.id === undefined) {
            return
        }

        try {
            setIsSigningIn(true)
            const loginResponse = await RecordFactory.getInstance().login(
                { associatedAccountUserID: user.id },
                queryClient,
                rollbar
            )
            setIsSigningIn(false)
            if (!loginResponse.ok) {
                return
            }

            navigate(
                defaultPathForLoginResponse(loginResponse.value) +
                    (selectedItem?.requiresUpgrade
                        ? `?back=${encodeURIComponent('/login/accounts')}`
                        : '')
            )
        }
        catch (error) {
            setIsSigningIn(false)
            logAndDisplayError(error)
        }
    }

    return (
        <WorkflowPage
            title="Welcome back!"
            onClose={null}
            footerContent={
                <div className="flex items-center justify-end">
                    <Button
                        intent="primary"
                        disabled={selectedUserID === undefined}
                        isLoading={isSigningIn}
                        onClick={handleSwitchAccount}
                    >
                        Next
                    </Button>
                </div>
            }
        >
            <Paragraph size="md" className="mt-4 text-gray-600">
                Select your account to continue
            </Paragraph>

            <WorkflowPage.Card className="mt-12">
                {listItems !== undefined ? (
                    <SelectList
                        selectionType="single"
                        items={listItems}
                        selectedItems={selectedItem ? [selectedItem] : []}
                        onSelectItems={(items) => setSelectedUserID(items[0]?.id)}
                    />
                ) : (
                    <div className="flex items-center justify-center">
                        <Spinner size={64} />
                    </div>
                )}
            </WorkflowPage.Card>
        </WorkflowPage>
    )
}
